/**
 * ÖZEL IRKLAR — Toplu aktarım
 *
 * Bu dosyadaki ırkların kural metinleri `docs/CONTENT/races.raw.md` içinde
 * KESİNTİSİZ olarak mevcuttur. Burada yalnızca mekaniksel olarak MODELLENEBİLİR
 * olan kısımlar (yetenek bonusu, hız, seçim, seviyeli özellik) yapılandırılmış
 * olarak yazılmıştır.
 *
 * KURAL: Aktif/koşullu yetenekler ("ağır zırh giymiyorken uçuş") METİN olarak
 * bırakılmıştır. Motor koşul dili çalıştırmaz — D&D Beyond da öyle yapar.
 */

import type { AbilityKey, ChoiceOption, RaceEntry } from '@tttrpg/rules'

/** Tekrarlayan alanları toparlamak için ortak varsayılanlar. */
const BOS = {
  abilityBonuses: {},
  asiChoices: {},
  asi: 0,
  asiChoiceOptions: [] as AbilityKey[],
  acBase: 10,
  darkvision: 0,
  cantrips: [],
  feat: null,
  innateSpells: [],
} satisfies Partial<RaceEntry>

// ===========================================================================
// İNSAN
// ===========================================================================

/** Seviye 1 beceri seçimi: kural metninde adı geçen 3 beceri. */
const INSAN_BECERI: ChoiceOption[] = [
  {
    id: 'investigation',
    name: 'Investigation',
    nameTr: 'Soruşturma',
    description: 'Eşya, iz ve gizem çözme becerisinde proficiency kazanırsın.',
    effects: { skillProficiencies: ['skill.investigation'] },
  },
  {
    id: 'history',
    name: 'History',
    nameTr: 'Tarih',
    description: 'Geçmiş olayları anlama becerisinde proficiency kazanırsın.',
    effects: { skillProficiencies: ['skill.history'] },
  },
  {
    id: 'arcana',
    name: 'Arcana',
    nameTr: 'Arkan Bilgi',
    description: 'Büyü ve gizemli olayları anlama becerisinde proficiency kazanırsın.',
    effects: { skillProficiencies: ['skill.arcana'] },
  },
]

/** Seviye 14: hangi kurtarma atışında proficiency. */
const INSAN_KURTARMA: ChoiceOption[] = (['str', 'dex', 'con', 'int', 'wis', 'cha'] as const).map(
  (key) => ({
    id: key,
    name: key,
    nameTr: {
      str: 'Güç',
      dex: 'Çeviklik',
      con: 'Dayanıklılık',
      int: 'Zekâ',
      wis: 'Algı',
      cha: 'Karizma',
    }[key],
    description:
      'Bu kurtarma atışında proficiency kazanırsın (her Uzun Dinlenme değiştirebilirsin).',
    effects: { savingThrowProficiencies: [key] },
  }),
)

export const INSAN: RaceEntry = {
  id: 'insan',
  name: 'İnsan',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [
    {
      id: 'insan-uyarlanabilir-miras-beceri',
      label: 'Adaptable Heritage (Skill)',
      labelTr: 'Uyarlanabilir Miras (Beceri)',
      description: 'Investigation, History veya Arcana’dan birinde proficiency kazan.',
      min: 1,
      max: 1,
      options: INSAN_BECERI,
    },
    {
      // ⚠️ "Bir Origin Feat kazan" — kural metninde feat LİSTESİ yok.
      // Liste gelene kadar grup yapı olarak hazır bekliyor.
      id: 'insan-origin-feat',
      label: 'Adaptable Heritage (Origin Feat)',
      labelTr: 'Uyarlanabilir Miras (Origin Feat)',
      description: 'Bir Origin Feat seç.',
      min: 1,
      max: 1,
      pending: true,
      pendingCount: 3,
      options: [],
    },
    {
      id: 'insan-uyarlanabilir-savunma-14',
      label: 'Adaptable Defense',
      labelTr: 'Uyarlanabilir Savunma (Seviye 14)',
      description: 'Bir kurtarma atışı türünde proficiency seç.',
      min: 1,
      max: 1,
      grantedAtLevel: 14,
      options: INSAN_KURTARMA,
    },
  ],
  features: [
    {
      id: 'insan-uyarlanabilir-miras',
      name: 'Uyarlanabilir Miras',
      grantedAtLevel: 1,
      description:
        '1 Origin Feat. Investigation / History / Arcana’dan birinde proficiency. Uzun Dinlenme bitince Heroic Inspiration kazanırsın.',
    },
    {
      id: 'insan-hizli-uyum',
      name: 'Hızlı Uyum',
      grantedAtLevel: 3,
      description:
        'Uzun Dinlenme bitince bir Skill veya Tool seç; sonraki Uzun Dinlenme’ye kadar proficiency.',
    },
    {
      id: 'insan-insan-inadi',
      name: 'İnsan İnadı',
      grantedAtLevel: 5,
      description: 'Başarısız bir kurtarma atışından sonra Reaction ile atışa 1d4. PB / LR.',
    },
    {
      id: 'insan-cok-yonlu-ustalik',
      name: 'Çok Yönlü Ustalık',
      grantedAtLevel: 7,
      description: 'Bir Skill veya Tool seç; Expertise kazan.',
    },
    {
      id: 'insan-eski-dunya-ustaligi',
      name: 'Eski Dünya Ustalığı',
      grantedAtLevel: 9,
      description:
        'Seviye 1’de seçtiğin beceride Expertise. Doğal 1 otomatik başarısızlık sayılmaz.',
    },
    {
      id: 'insan-azimli-direnis',
      name: 'Azimli Direniş',
      grantedAtLevel: 10,
      description: 'HP 0’a düşünce Death Save atmadan 1 HP ile kalkarsın. 1 / LR.',
    },
    {
      id: 'insan-ilham-veren-varlik',
      name: 'İlham Veren Varlık',
      grantedAtLevel: 11,
      description:
        'Kısa Dinlenme’de en fazla PB müttefik seç; her biri LR’a kadar Heroic Inspiration alır. PB / LR.',
    },
    {
      id: 'insan-son-dakika-cozumu',
      name: 'Son Dakika Çözümü',
      grantedAtLevel: 13,
      description: 'Initiative atışında Heroic Inspiration’ın yoksa 1 kazanırsın. 1 / LR.',
    },
    {
      id: 'insan-insan-ustunlugu',
      name: 'İnsan Üstünlüğü',
      grantedAtLevel: 15,
      description: 'Zarı gördükten sonra +PB ekleyebilirsin. PB / LR.',
    },
    {
      id: 'insan-pes-etmem',
      name: 'Pes Etmem',
      grantedAtLevel: 17,
      description: '0 HP’ye düşecekken 1 HP’de kalırsın. 1 / LR.',
    },
    {
      id: 'insan-efsanevi-kararlilik',
      name: 'Efsanevi Kararlılık',
      grantedAtLevel: 18,
      description: 'Başarısız bir d20 testini 20 olarak değiştir. 1 / LR.',
    },
    {
      id: 'insan-evrensel-ustalik',
      name: 'Evrensel Ustalık',
      grantedAtLevel: 19,
      description: 'İki farklı Skill veya Tool’da kalıcı Expertise.',
    },
  ],
}

// ===========================================================================
// ELİATROP
// ===========================================================================

export const ELIATROP: RaceEntry = {
  id: 'eliatrop',
  name: 'Eliatrop',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [],
  features: [
    {
      id: 'eliatrop-wakfu-portali',
      name: 'Wakfu Portali',
      grantedAtLevel: 1,
      description: 'Bonus eylemle 30 ft içinde iki nokta arası portal. PB / LR.',
    },
    {
      id: 'eliatrop-wakfu-hassasiyeti',
      name: 'Wakfu Hassasiyeti',
      grantedAtLevel: 3,
      description: '30 ft içinde portal, Wakfu anomalisi veya yoğun büyü kaynağını hissedersin.',
    },
    {
      id: 'eliatrop-portal-adimi',
      name: 'Portal Adımı',
      grantedAtLevel: 5,
      description: 'Sana isabet eden saldırıda Reaction ile 15 ft teleport. PB / LR.',
    },
    {
      id: 'eliatrop-wakfu-darbesi',
      name: 'Wakfu Darbesi',
      grantedAtLevel: 7,
      description: 'Melee isabetinde 1d6 Force (11. sv 2d6, 17. sv 3d6). PB / LR.',
    },
    {
      id: 'eliatrop-gelismis-portal',
      name: 'Gelişmiş Portal',
      grantedAtLevel: 9,
      description: 'Portal menzili 60 ft; portaldan ranged saldırı yapılabilir.',
    },
    {
      id: 'eliatrop-portal-savunmasi',
      name: 'Portal Savunması',
      grantedAtLevel: 11,
      description: 'Portaldan geçtiğin turun sonuna kadar AC +2.',
    },
    {
      id: 'eliatrop-wakfu-yenilenmesi',
      name: 'Wakfu Yenilenmesi',
      grantedAtLevel: 13,
      description: 'Kısa Dinlenme sonunda 1 harcanmış portal kullanımı geri gelir.',
    },
    {
      id: 'eliatrop-portal-zinciri',
      name: 'Portal Zinciri',
      grantedAtLevel: 15,
      description: 'Aynı turda ikinci portal; ek eylem gerektirmez. 1 / tur.',
    },
    {
      id: 'eliatrop-boyut-kapisi',
      name: 'Boyut Kapısı',
      grantedAtLevel: 17,
      description: 'Eylemle 120 ft içinde portal; bir müttefik de geçebilir. 1 / LR.',
    },
    {
      id: 'eliatrop-wakfu-ustasi',
      name: 'Wakfu Ustası',
      grantedAtLevel: 19,
      description: 'Portal/Wakfu özelliği kullanınca Heroic Inspiration. 1 / LR.',
    },
  ],
}

// ===========================================================================
// ŞEKER HALKI
// ===========================================================================

export const SEKER_HALKI: RaceEntry = {
  id: 'seker-halki',
  name: 'Şeker Halkı',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [],
  features: [
    {
      id: 'seker-seker-bedeni',
      name: 'Şeker Bedeni',
      grantedAtLevel: 1,
      description:
        'Zehir direnci. Zehirli durumuna karşı kurtarma atışlarında kazanç. Kavrama denemelerinden kaçarken kazanç. Dar alanlardan esneyerek geçebilirsin.',
    },
    {
      id: 'seker-yapiskan-tutus',
      name: 'Yapışkan Tutuş',
      grantedAtLevel: 3,
      description:
        'Itme veya zorla hareket ettirilme etkisine karşı kurtarma/denemede kazanç. PB / LR.',
    },
    {
      id: 'seker-seker-onarimi',
      name: 'Şeker Onarımı',
      grantedAtLevel: 5,
      description: 'Bonus eylemle 1d6 + PB iyileşme. 1 / SR veya LR.',
    },
    {
      id: 'seker-akiskan-form',
      name: 'Akışkan Form',
      grantedAtLevel: 9,
      description:
        'Bonus eylem 1 dk: fırsat saldırılarına karşı AC +2, grappled durumundan ücretsiz çıkış denemesi, 1 inç aralıktan geçme. 1 / LR.',
    },
    {
      id: 'seker-tatli-yenilenme',
      name: 'Tatlı Yenilenme',
      grantedAtLevel: 13,
      description: 'Şeker Onarımı 2d8 + PB olur ve Poisoned durumunu kaldırabilir.',
    },
    {
      id: 'seker-seker-devi',
      name: 'Şeker Devi',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk: Büyük boy, +5 ft menzil, Güç deneme/kurtarma kazancı, kesici-delici-yuvarlayan hasara direnç. 1 / LR.',
      // Bonus eylemle aktif: kalıcı değil. Bu yüzden varsayılan özellik olarak
      // YAZILMAZ — kullanıldığında geçerlidir. (Bilinçli kapsam dışı.)
    },
  ],
}

// ===========================================================================
// BALIK ADAMLAR
// ===========================================================================

export const BALIK_ADAMLARI: RaceEntry = {
  id: 'balik-adamlari',
  name: 'Balık Adamlar',
  size: 'Orta',
  reach: 5,
  speed: 25,
  ...BOS,
  darkvision: 60,
  choices: [],
  features: [
    {
      id: 'balik-deniz-halki',
      name: 'Deniz Halkı',
      grantedAtLevel: 1,
      description:
        'Kara hızı 25 ft, yüzme hızı 40 ft, amfibi. Su altında Athletics denemelerinde kazanç. Karanlık görüş 60 ft.',
    },
    {
      id: 'balik-akinti-sicramasi',
      name: 'Akıntı Sıçraması',
      grantedAtLevel: 3,
      description: 'Suda Bonus eylemle Dash; karada 10 ft ekstra hareket. PB / LR.',
    },
    {
      id: 'balik-derin-su-bedeni',
      name: 'Derin Su Bedeni',
      grantedAtLevel: 5,
      description:
        'Soğuk direnci. Derin su basıncı ve uzun yüzme Dayanıklılık kurtarma atışlarında kazanç.',
    },
    {
      id: 'balik-gelgit-kacisi',
      name: 'Gelgit Kaçışı',
      grantedAtLevel: 9,
      description:
        'Sana isabet eden saldırıda Reaction: suda 20 ft, karada 10 ft fırsat saldırısı tetiklemeden hareket. PB / LR.',
    },
    {
      id: 'balik-su-hukumu',
      name: 'Su Hükümü',
      grantedAtLevel: 13,
      description: 'Control Water büyüsünü 1 / LR yuvasız kullanırsın. Büyü yeteneği: Algı.',
      innateSpells: [
        { grantedAtLevel: 13, spellIds: ['control-water'], uses: { count: 1, recharge: 'long' } },
      ],
    },
    {
      id: 'balik-girdap-cagrisi',
      name: 'Girdap Çağrısı',
      grantedAtLevel: 17,
      description:
        'Eylemle 15 ft yarıçapında girdap: Güç kurtarma atışı, başarısızlık 4d6 ve 15 ft çekilme. 1 / LR.',
    },
  ],
}

// ===========================================================================
// MANTAR HALKI
// ===========================================================================

export const MANTAR_HALKI: RaceEntry = {
  id: 'mantar-halki',
  name: 'Mantar Halkı',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  darkvision: 60,
  choices: [],
  features: [
    {
      id: 'mantar-spor-halki',
      name: 'Spor Halkı',
      grantedAtLevel: 1,
      description:
        'Karanlık görüş 60 ft, zehir direnci, zehirli durumuna karşı kazanç, 30 ft spor ağı telepatisi.',
    },
    {
      id: 'mantar-genis-spor-agi',
      name: 'Geniş Spor Ağı',
      grantedAtLevel: 3,
      description:
        'Spor ağı menzili 60 ft; mantar/bitki kökenli yaratıklarla basit duygu ve imgeler paylaşımı.',
    },
    {
      id: 'mantar-iyilestirici-spor',
      name: 'İyileştirici Spor',
      grantedAtLevel: 5,
      description:
        'Eylemle 15 ft içindeki yaratığa 1d8 + PB iyileştirme. Construct ve Undead etkilenmez. 1 / SR veya LR.',
    },
    {
      id: 'mantar-misel-adimi',
      name: 'Misel Adımı',
      grantedAtLevel: 9,
      description: 'Organik yüzeye yakınken Bonus eylemle 30 ft teleport. PB / LR.',
    },
    {
      id: 'mantar-koloni-hafizasi',
      name: 'Koloni Hafızası',
      grantedAtLevel: 13,
      description:
        'Nature veya Survival’da Expertise. Toprağa 1 dk değerek son 24 saatteki büyük olaya dair bulanık iz. 1 / LR.',
    },
    {
      id: 'mantar-spor-ciceklenmesi',
      name: 'Spor Çiçeklenmesi',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk 10 ft aura: Dayanıklılık kurtarma atışı, başarısızlık Poisoned. 1 / LR.',
    },
  ],
}

// ===========================================================================
// BULUT HALKI
// ===========================================================================

export const BULUT_HALKI: RaceEntry = {
  id: 'bulut-halki',
  name: 'Bulut Halkı',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [],
  features: [
    {
      id: 'bulut-bulut-suzulmesi',
      name: 'Bulut Süzülmesi',
      grantedAtLevel: 1,
      description:
        'Bonus eylemle turun sonuna kadar uçuş hızı 30 ft. Düşme hasarı mesafesi 30 ft azalır. PB / LR.',
    },
    {
      id: 'bulut-ruzgar-bedeni',
      name: 'Rüzgâr Bedeni',
      grantedAtLevel: 3,
      description:
        'Kavrama denemelerinden kaçarken kazanç. Zorlu arazi sayılan sisi/bulutu görmezden gelirsin.',
    },
    {
      id: 'bulut-dagilan-form',
      name: 'Dağılan Form',
      grantedAtLevel: 5,
      description:
        'Bonus eylem 1 dk: sıkışmada ek hareket cezası yok, fırsat saldırılarına karşı AC +1. 1 / LR.',
    },
    {
      id: 'bulut-gercek-ucus',
      name: 'Gerçek Uçuş',
      grantedAtLevel: 9,
      description:
        'Ağır zırh giymiyorken sürekli uçuş hızı 30 ft. Seviye 1 yeteneği bu turda 40 ft.’e çıkar.',
    },
    {
      id: 'bulut-buluta-dagil',
      name: 'Buluta Dağıl',
      grantedAtLevel: 13,
      description:
        'Hasar alınca Reaction: hasarı 1d10 + PB azalt, 10 ft fırsat saldırısı tetiklemeden hareket. PB / LR.',
    },
    {
      id: 'bulut-firtina-kalbi',
      name: 'Fırtına Kalbi',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk: uçuş hızı 60 ft, yıldırım/gök gürültüsü direnci, fırsat saldırılarında dezavantaj. 1 / LR.',
    },
  ],
}

// ===========================================================================
// GÖLGE HALKI
// ===========================================================================

export const GOLGE_HALKI: RaceEntry = {
  id: 'golge-halki',
  name: 'Gölge Halkı',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  darkvision: 120,
  choices: [],
  features: [
    {
      id: 'golge-gece-cocugu',
      name: 'Gece Çocuğu',
      grantedAtLevel: 1,
      description: 'Karanlık görüş 120 ft. Loş ışık/Karanlıkta Bonus eylemle Hide. PB / LR.',
    },
    {
      id: 'golge-golge-kaymasi',
      name: 'Gölge Kayması',
      grantedAtLevel: 3,
      description:
        'Bonus eylemle 30 ft gölgeden gölgeye teleport. PB / LR. Radiant hasar sonrası bir sonraki tur kullanılamaz.',
    },
    {
      id: 'golge-golge-perdesi',
      name: 'Gölge Perdesi',
      grantedAtLevel: 5,
      description: 'Loş ışık/Karanlıkta sana yapılan saldırıya Reaction ile dezavantaj. PB / LR.',
    },
    {
      id: 'golge-uzun-golge',
      name: 'Uzun Gölge',
      grantedAtLevel: 9,
      description:
        'Gölge Kayması menzili 60 ft. 1 / LR, yanındaki gönüllü yaratığı da taşıyabilirsin.',
    },
    {
      id: 'golge-yari-golge-beden',
      name: 'Yarı Gölge Beden',
      grantedAtLevel: 13,
      description:
        'Nekrotik direnç. Bonus eylem 1 dk: alanları zorlu arazi gibi geç, 1 inç aralıktan geç. 1 / LR.',
    },
    {
      id: 'golge-gece-hukumdari',
      name: 'Gece Hükümdarı',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk: Gölge Kayması her tur ücretsiz, teleport sonrası ilk saldırıda avantaj, Hide bonus eylem. 1 / LR.',
    },
  ],
}

// ===========================================================================
// BİTKİ HALKI
// ===========================================================================

const BITKI_BECERI: ChoiceOption[] = [
  {
    id: 'nature',
    name: 'Nature',
    nameTr: 'Doğa Bilgisi',
    description: 'Doğa Bilgisi becerisinde proficiency kazanırsın.',
    effects: { skillProficiencies: ['skill.nature'] },
  },
  {
    id: 'survival',
    name: 'Survival',
    nameTr: 'Hayatta Kalma',
    description: 'Hayatta Kalma becerisinde proficiency kazanırsın.',
    effects: { skillProficiencies: ['skill.survival'] },
  },
]

export const BITKI_HALKI: RaceEntry = {
  id: 'bitki-halki',
  name: 'Bitki Halkı',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  cantrips: ['druidcraft'],
  choices: [
    {
      id: 'bitki-doga-veya-hayatta-kalma',
      label: 'Green Birth',
      labelTr: 'Yeşil Doğum (Beceri)',
      description: 'Nature veya Survival’dan birinde proficiency seç.',
      min: 1,
      max: 1,
      options: BITKI_BECERI,
    },
  ],
  features: [
    {
      id: 'bitki-yesil-dogum',
      name: 'Yeşil Doğum',
      grantedAtLevel: 1,
      description:
        'Nature veya Survival proficiency, Druidcraft, fotosentetik toparlanma (uygun SR sonrası 2 × PB ekstra HP, 1 / LR).',
    },
    {
      id: 'bitki-bitki-dili',
      name: 'Bitki Dili',
      grantedAtLevel: 3,
      description: 'Speak with Plants büyüsünü 1 / LR yuvasız kullanırsın. Büyü yeteneği: Algı.',
      innateSpells: [
        {
          grantedAtLevel: 3,
          spellIds: ['speak-with-plants'],
          uses: { count: 1, recharge: 'long' },
        },
      ],
    },
    {
      id: 'bitki-sarmasik-savunmasi',
      name: 'Sarmaşık Savunması',
      grantedAtLevel: 5,
      description:
        '10 ft içinde bir düşman yaklaşınca Reaction: Güç kurtarma atışı, başarısızlıkta hız 0. PB / LR.',
    },
    {
      id: 'bitki-yesil-yenilenme',
      name: 'Yeşil Yenilenme',
      grantedAtLevel: 9,
      description:
        'Kısa Dinlenme’de Hit Dice ile iyileşirken ilk zarı maksimum sayabilirsin. 1 / LR.',
    },
    {
      id: 'bitki-koruluk-cagrisi',
      name: 'Koruluk Çağrısı',
      grantedAtLevel: 13,
      description: 'Plant Growth büyüsünü 1 / LR yuvasız kullanırsın. Büyü yeteneği: Algı.',
      innateSpells: [
        { grantedAtLevel: 13, spellIds: ['plant-growth'], uses: { count: 1, recharge: 'long' } },
      ],
    },
    {
      id: 'bitki-kadim-koruluk-formu',
      name: 'Kadim Koruluk Formu',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk: 3 × PB geçici HP, +5 ft menzil, 10 ft zorlu arazi, Güç/Dayanıklılık kurtarma kazancı. 1 / LR.',
    },
  ],
}

// ===========================================================================
// ZAMAN KAYMASI GEZGİNİ
// ===========================================================================

export const ZAMAN_KAYMASI: RaceEntry = {
  id: 'zaman-kaymasi',
  name: 'Zaman Kayması Gezgini',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [],
  features: [
    {
      id: 'zaman-onsezi-parlamasi',
      name: 'Önsezi Parlaması',
      grantedAtLevel: 1,
      description: 'Reaction ile 30 ft içindeki d20 testine 1d4 ekle. PB / LR.',
    },
    {
      id: 'zaman-zamansal-refleks',
      name: 'Zamansal Refleks',
      grantedAtLevel: 3,
      description:
        'Initiative atışına PB bonus. 1 / LR. Sürprizden kurtulduğun ilk turda hız +10 ft.',
    },
    {
      id: 'zaman-calinmis-saniye',
      name: 'Çalınmış Saniye',
      grantedAtLevel: 5,
      description: 'Bonus eylemle Dash veya Disengage. PB / LR.',
    },
    {
      id: 'zaman-ikinci-ihtimal',
      name: 'İkinci İhtimal',
      grantedAtLevel: 9,
      description: 'Başarısız bir d20 testini yeniden at; yeni sonucu kullan. 1 / LR.',
    },
    {
      id: 'zaman-zaman-kaymasi',
      name: 'Zaman Kayması',
      grantedAtLevel: 13,
      description:
        'Sana isabet eden saldırıda Reaction: hasarı yarıya indir, 15 ft teleport. 1 / LR.',
    },
    {
      id: 'zaman-calinmis-tur',
      name: 'Çalınmış Tur',
      grantedAtLevel: 17,
      description:
        'Turunda bir ek eylem: silah saldırısı, Dash, Disengage, Help, Use an Object veya cantrip. 1 / LR.',
    },
  ],
}

// ===========================================================================
// KRİSTAL HALKI
//
// DİKKAT: Kural metninde iki kristal tipi ADLANDIRILMIŞ ("Zırh Kristalı" veya
// "Prizmatik Kristal") ama ikisinin mekanik farkı YAZILMAMIŞ. Seçenekler gerçek
// (metinde adı geçiyor) ama etkileri boş bırakıldı — farkı uydurmak yerine
// metin geldiğinde doldurulacak.
// ===========================================================================

export const KRISTAL_HALKI: RaceEntry = {
  id: 'kristal-halki',
  name: 'Kristal Halkı',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [
    {
      id: 'kristal-tipi',
      label: 'Crystal Form',
      labelTr: 'Kristal Tipi',
      description:
        'Zırh Kristalı veya Prizmatik Kristal seç. İkisinin mekanik farkı kural metninde belirtilmemiş — seçim şimdilik etkisiz.',
      min: 1,
      max: 1,
      options: [
        {
          id: 'zirh-kristali',
          name: 'Armor Crystal',
          nameTr: 'Zırh Kristalı',
          description: 'Kural metninde adı geçiyor; mekanik farkı henüz yazılmamış.',
          effects: {},
        },
        {
          id: 'prizmatik-kristal',
          name: 'Prismatic Crystal',
          nameTr: 'Prizmatik Kristal',
          description: 'Kural metninde adı geçiyor; mekanik farkı henüz yazılmamış.',
          effects: {},
        },
      ],
    },
  ],
  features: [
    {
      id: 'kristal-kristal-bicim',
      name: 'Kristal Biçim',
      grantedAtLevel: 1,
      description:
        'Zırh Kristalı veya Prizmatik Kristal seç. Enerji Depolama: uygun energy hasar aldığında Reaction ile sonraki d20 testine 1d4 ekle. PB / LR.',
    },
    {
      id: 'kristal-rezonans-sezgisi',
      name: 'Rezonans Sezgisi',
      grantedAtLevel: 3,
      description: 'Arkan Bilgi veya Soruşturma proficiency. Kristal/Wakfu incelemede avantaj.',
    },
    {
      id: 'kristal-guclu-depolama',
      name: 'Güçlü Depolama',
      grantedAtLevel: 5,
      description: 'Enerji Depolama zarı 1d6 olur ve damage roll’a da eklenebilir.',
    },
    {
      id: 'kristal-kristal-patlamasi',
      name: 'Kristal Patlaması',
      grantedAtLevel: 9,
      description:
        'Depolanan enerjiyi 15 ft konide boşalt: Çeviklik kurtarma atışı, 3d6 hasar, başarılıysa yarı.',
    },
    {
      id: 'kristal-prizmatik-savunma',
      name: 'Prizmatik Savunma',
      grantedAtLevel: 13,
      description:
        'Asit/soğuk/ateş/zor/şımkır/nektrotik/radyant hasarda Reaction ile direnç. PB / LR.',
    },
    {
      id: 'kristal-asiri-yuk-modu',
      name: 'Aşırı Yük Modu',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk: 2 enerji yükü, Depolama zarı 1d10, Kristal Patlaması 5d6. 1 / LR.',
    },
  ],
}
// ===========================================================================
// İOP
// ===========================================================================

export const IOP: RaceEntry = {
  id: 'iop',
  name: 'İop',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [],
  features: [
    {
      id: 'iop-iop-sicramisi',
      name: 'Iop Sıçrayışı',
      grantedAtLevel: 1,
      description: 'Bonus eylemle 20 ft sıçrama/konumlanma; fırsat saldırısı tetiklemez. PB / LR.',
    },
    {
      id: 'iop-savas-coskusu',
      name: 'Savaş Coşkusu',
      grantedAtLevel: 1,
      description:
        'Hasar aldıktan sonraki Güç saldırısında veya Athletics denemesinde avantaj. PB / LR.',
    },
    {
      id: 'iop-meydan-okuma',
      name: 'Meydan Okuma',
      grantedAtLevel: 3,
      description:
        'Bonus eylem 30 ft: Algı kurtarma atışı, başarısızlıkta senden başka hedeflere dezavantaj. 1 / SR veya LR.',
    },
    {
      id: 'iop-son-yumruk',
      name: 'Son Yumruk',
      grantedAtLevel: 5,
      description:
        'Yarı HP veya altındayken melee isabetinde ekstra PB hasar; sonraki tur başına kadar AC −1. 1 / LR.',
    },
    {
      id: 'iop-yildirim-darbesi',
      name: 'Yıldırım Darbesi',
      grantedAtLevel: 9,
      description:
        'Iop Sıçrayışı sonrası indiğinde 10 ft çevrede Güç kurtarma atışı: 2d6 gök gürültüsü, 10 ft itilme. 1 / LR.',
    },
    {
      id: 'iop-kirilmaz-irable',
      name: 'Kırılmaz İrade',
      grantedAtLevel: 13,
      description:
        'Başarısız Zekâ/Algı/Karizma kurtarma atışını yeniden at; yeni sonucu kullan. 1 / LR.',
    },
    {
      id: 'iop-iop-un-gabagi',
      name: 'Iop’un Gazabı',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk: Iop Sıçrayışı her tur ücretsiz, ilk melee isabette ekstra PB, korkuya bağışıklık. 1 / LR.',
    },
  ],
}

// ===========================================================================
// CRA
// ===========================================================================

const CRA_BECERI: ChoiceOption[] = [
  {
    id: 'perception',
    name: 'Perception',
    nameTr: 'Algı',
    description: 'Algı becerisinde proficiency kazanırsın.',
    effects: { skillProficiencies: ['skill.perception'] },
  },
  {
    id: 'survival',
    name: 'Survival',
    nameTr: 'Hayatta Kalma',
    description: 'Hayatta Kalma becerisinde proficiency kazanırsın.',
    effects: { skillProficiencies: ['skill.survival'] },
  },
]

export const CRA: RaceEntry = {
  id: 'cra',
  name: 'Cra',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [
    {
      id: 'cra-perception-veya-survival',
      label: 'Horizon Marksman (Skill)',
      labelTr: 'Ufuk Nişancısı (Beceri)',
      description: 'Perception veya Survival’dan birinde proficiency seç.',
      min: 1,
      max: 1,
      options: CRA_BECERI,
    },
  ],
  features: [
    {
      id: 'cra-ufuk-nisancisi',
      name: 'Ufuk Nişancısı',
      grantedAtLevel: 1,
      description:
        'Kısa yay ve uzun yay proficiency. Perception veya Survival proficiency. Tur başında hareket etmeden önce yaptığın ilk ranged saldırıya +1.',
    },
    {
      id: 'cra-geri-sicrama',
      name: 'Geri Siçrama',
      grantedAtLevel: 3,
      description:
        '5 ft içindeki düşman sana doğru bitirince Reaction ile 10 ft fırsat saldırısı tetiklemeden hareket. PB / LR.',
    },
    {
      id: 'cra-isaret-oku',
      name: 'İşaret Oku',
      grantedAtLevel: 5,
      description:
        'Ranged isabetinde hedefi işaretle; müttefikin ilk saldırısında avantaj. 1 / SR veya LR.',
    },
    {
      id: 'cra-ruzgar-oku',
      name: 'Rüzgâr Oku',
      grantedAtLevel: 5,
      description:
        'Ranged isabetinde Büyük veya küçük hedefe Güç kurtarma atışı, başarısızlıkta 10 ft it. PB / LR.',
    },
    {
      id: 'cra-coklu-ok',
      name: 'Çoklu Ok',
      grantedAtLevel: 9,
      description:
        'Ranged saldırıdan sonra aynı silahla ikinci bir hedefe saldırı; ikinci hedef ilkinin 20 ft içinde. PB / LR.',
    },
    {
      id: 'cra-kartal-gogu',
      name: 'Kartal Gözü',
      grantedAtLevel: 13,
      description:
        'Bonus eylem 1 dk: uzun menzil dezavantajı yok, Algı denemelerinde avantaj, yarı örtü hedefe bonus vermez. 1 / LR.',
    },
    {
      id: 'cra-wakfu-oku',
      name: 'Wakfu Oku',
      grantedAtLevel: 17,
      description:
        'Ranged saldırıdan önce oku Wakfu ile doldur: normal hasar + 3d8 Force; hedef AC’sindeki kalkan dışı örtü bonusunun 2’sini yok say. 1 / LR.',
    },
  ],
}
